import type { ViewStyle } from 'react-native'

import { Box } from '../theme/restyle'
import { FALLBACK_ASSET_LOGO } from './assetLogos'
import { FunIcon } from './FunIcon'
import { chainIconUri } from './funIconUri'

/**
 * The chain-logo cluster shown in the center of the deposit QR (Figma: qr-code
 * → `icon/chain`). It reflects the selected network: a single network renders
 * one large logo (e.g. Solana), a multi-chain network renders a 2×2 grid of
 * small logos (e.g. EVM: Ethereum / Aptos / Base / BNB). Logos resolve from CDN
 * chain keys OR full image URLs so the package stays asset-free.
 *
 * The QR behind it is drawn at high error-correction ({@link QrCode} `ecl="H"`),
 * so this central occlusion stays scannable.
 */
export interface QrChainClusterProps {
  /**
   * Logos for the selected network — either CDN chain **keys** (e.g.
   * `['ethereum', 'bsc']`) or full image **URLs** (`@funkit/chains` `iconUrl`s).
   * One entry → a single logo; 2–4 → a 2×2 grid.
   */
  iconKeys: string[]
  /** Max logos in the grid before the rest are dropped (Figma shows 4). */
  max?: number
}

/** A full URL is used as-is; anything else is treated as a CDN chain key. */
function toIconUri(icon: string): string {
  return icon.startsWith('http') ? icon : chainIconUri(icon)
}

// Smaller logos with clear padding inside the quiet-zone card, so the center
// occludes fewer QR modules (denser read) and the icons don't touch the edge.
// Figma `icon/chain` card (node 12782:5552): 46px quiet-zone card, 12px radius
// (border/radius/element). The 2×2 grid is centered inside it, so the icons
// keep a clean margin from the card edge.
const CARD_SIZE = 46
const CARD_RADIUS = 12
// Single logo fills the card with the same ~6px margin the 2×2 grid keeps.
const SINGLE_SIZE = 34
const GRID_SIZE = 16
const GRID_GAP = 3

export function QrChainCluster({ iconKeys, max = 4 }: QrChainClusterProps) {
  const keys = iconKeys.slice(0, max)
  const first = keys[0]
  if (first == null) {
    return null
  }

  if (keys.length === 1) {
    return (
      <Box alignItems="center" justifyContent="center" style={card}>
        <Box style={ring(SINGLE_SIZE)}>
          <FunIcon
            uri={toIconUri(first)}
            size={SINGLE_SIZE}
            fallbackUri={FALLBACK_ASSET_LOGO}
          />
        </Box>
      </Box>
    )
  }

  return (
    <Box alignItems="center" justifyContent="center" style={card}>
      <Box flexDirection="row" flexWrap="wrap" style={grid}>
        {keys.map((key) => (
          <Box key={key} style={ring(GRID_SIZE)}>
            <FunIcon
              uri={toIconUri(key)}
              size={GRID_SIZE}
              fallbackUri={FALLBACK_ASSET_LOGO}
            />
          </Box>
        ))}
      </Box>
    </Box>
  )
}

// Cap the row at two columns so 3–4 logos wrap into a 2×2.
const grid: ViewStyle = {
  width: GRID_SIZE * 2 + GRID_GAP,
  gap: GRID_GAP,
}
// The white rounded-square "quiet zone" behind the cluster (Figma `icon/chain`).
// The QR is drawn at high error-correction so this central occlusion stays
// scannable — without it the QR pattern bleeds through the gaps between the
// circular logos. Padded so a little white shows around the 2×2 grid.
const card: ViewStyle = {
  width: CARD_SIZE,
  height: CARD_SIZE,
  borderRadius: CARD_RADIUS,
  backgroundColor: '#FFFFFF',
}
// A logo clipped to a circle (no border — the chain SVGs carry their own
// shape). The fill is an opaque white — the QR is always drawn on white cells
// ({@link QrCode}), so transparent chain-logo SVGs need a white backing or the
// QR pattern bleeds through behind the icon.
const ring = (iconSize: number): ViewStyle => ({
  width: iconSize,
  height: iconSize,
  borderRadius: 999,
  backgroundColor: '#FFFFFF',
  overflow: 'hidden',
})
